import { useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Container, Text, Stack } from '@mantine/core'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { isAdminAuthenticated, refreshAdminSession } from '../../lib/auth'
import { AdminNavbar } from '../../components/AdminNavbar/AdminNavbar'
import { GuestTable } from '../../components/GuestTable/GuestTable'
import { GuestStats } from '../../components/GuestStats/GuestStats'
import { DietarySummary } from '../../components/DietarySummary/DietarySummary'
import { CommentsSummary } from '../../components/CommentsSummary/CommentsSummary'
import styles from './index.module.css'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
      pageSize: (search.pageSize as number | 'all') || 5,
      sortField: (search.sortField as string) || 'name',
      sortDirection: (search.sortDirection as 'asc' | 'desc') || 'asc',
      nameFilter: (search.nameFilter as string) || '',
      statusFilter: (search.statusFilter as string) || '',
    }
  },
})

function AdminDashboard() {
  const navigate = useNavigate()
  const search = Route.useSearch()

  useEffect(() => {
    // Check if admin is authenticated
    if (!isAdminAuthenticated()) {
      navigate({ to: '/admin/login' })
      return
    }

    // Refresh session on page load
    refreshAdminSession()

    // Set up interval to check session expiration
    const interval = setInterval(() => {
      if (!isAdminAuthenticated()) {
        navigate({ to: '/admin/login' })
      }
    }, 60000) // Check every minute

    return () => clearInterval(interval)
  }, [navigate])

  const handleTableStateChange = (updates: {
    page?: number
    pageSize?: number | 'all'
    sortField?: string
    sortDirection?: 'asc' | 'desc'
    nameFilter?: string
    statusFilter?: string
  }) => {
    navigate({
      to: '/admin',
      search: {
        ...search,
        ...updates,
      },
    })
  }

  return (
    <div className={styles.adminLayout}>
      <AdminNavbar />

      <Container size="xl" py="xl">
        <Stack gap="xl">
          <ResponsiveTitle order={1}>Admin-Dashboard</ResponsiveTitle>
          <Text size="lg" c="dimmed">
            Willkommen im Admin-Bereich. Hier können Sie Ihre Gästeliste verwalten.
          </Text>

          {/* Guest Statistics */}
          <GuestStats />

          {/* Guest Table */}
          <GuestTable
            page={search.page}
            pageSize={search.pageSize}
            sortField={search.sortField}
            sortDirection={search.sortDirection}
            nameFilter={search.nameFilter}
            statusFilter={search.statusFilter}
            onStateChange={handleTableStateChange}
          />

          {/* Dietary Summary */}
          <DietarySummary />

          {/* Comments Summary */}
          <CommentsSummary />
        </Stack>
      </Container>
    </div>
  )
}
